class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    // The modal can contain other type="button" elements (e.g. popular search
    // chips), so the close handler must target the dedicated close button.
    const closeButton = this.querySelector('.modal__close-button') || this.querySelector('button[type="button"]');
    closeButton.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    const details = event.target.closest('details');
    details.classList.remove('is-closing');
    details.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    this.sizePanel();

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);

    // Stop tracking the header position once the panel is closing.
    this.stopPanelTracking();

    // Play the panel's close animation before removing [open]; the search
    // panel opts in with a CSS transition on .is-closing.
    const panel = this.querySelector('.search-modal');
    if (panel && this.detailsContainer.hasAttribute('open')) {
      this.detailsContainer.classList.add('is-closing');
      const finish = () => {
        this.detailsContainer.classList.remove('is-closing');
        this.detailsContainer.removeAttribute('open');
      };
      const onEnd = (event) => {
        if (event.target !== panel) return;
        panel.removeEventListener('transitionend', onEnd);
        clearTimeout(this.closeFallback);
        finish();
      };
      panel.addEventListener('transitionend', onEnd);
      // Fallback in case transitionend never fires (e.g. reduced motion)
      this.closeFallback = setTimeout(() => {
        panel.removeEventListener('transitionend', onEnd);
        finish();
      }, 300);
    } else {
      this.detailsContainer.removeAttribute('open');
    }

    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }

  // The search panel is fixed to the header's real bottom edge. Sticky-header
  // transitions can skew the first measurement, so measure again once
  // animations settle, and keep it correct on resize/scroll while open.
  sizePanel() {
    const panel = this.querySelector('.search-modal');
    if (!panel) return;

    this.measurePanel = () => {
      const header = this.closest('.header-wrapper') || this;
      const bottom = Math.max(header.getBoundingClientRect().bottom, 0);
      panel.style.setProperty('--search-panel-top', `${Math.round(bottom)}px`);
    };

    this.measurePanel();
    setTimeout(this.measurePanel, 260);

    // Track scroll/resize only while open, throttled through rAF to avoid
    // layout thrash, and torn down in close() so nothing leaks.
    if (!this.onPanelReflow) {
      let ticking = false;
      this.onPanelReflow = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (this.detailsContainer.hasAttribute('open')) this.measurePanel();
        });
      };
    }
    window.addEventListener('resize', this.onPanelReflow);
    window.addEventListener('scroll', this.onPanelReflow, { passive: true });

    // Flag the panel as scrolled so the sticky search bar can show its frosted
    // backdrop only while content is actually scrolling beneath it.
    const content = panel.querySelector('.search-modal__content');
    if (content && !this.onPanelContentScroll) {
      this.panelContent = content;
      this.onPanelContentScroll = () => {
        content.classList.toggle('search-modal__content--scrolled', content.scrollTop > 2);
      };
      content.addEventListener('scroll', this.onPanelContentScroll, { passive: true });
    }
  }

  stopPanelTracking() {
    if (this.onPanelReflow) {
      window.removeEventListener('resize', this.onPanelReflow);
      window.removeEventListener('scroll', this.onPanelReflow);
    }
    if (this.panelContent && this.onPanelContentScroll) {
      this.panelContent.removeEventListener('scroll', this.onPanelContentScroll);
      this.panelContent.classList.remove('search-modal__content--scrolled');
      this.onPanelContentScroll = null;
    }
  }
}

customElements.define('details-modal', DetailsModal);
